import React, { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Upload, FileSpreadsheet, Check, X, ArrowRight, Package, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

function Stat({ label, value, mono }) {
  return (
    <div className="p-2 rounded-md bg-muted/30 border border-border">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm font-medium ${mono ? "font-mono" : ""}`}>{value || "-"}</div>
    </div>
  );
}

export default function OTACUploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/ots-comercial/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_OT_Comercial.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada");
    } catch {
      toast.error("Error al descargar la plantilla");
    }
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/ots-comercial/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data.preview);
      setStats(res.data.stats);
      toast.success("Archivo procesado correctamente");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al procesar el archivo");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setStats(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const confirmCreate = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const payload = {
        ...preview,
        items: preview.items,
      };
      const res = await api.post("/ots-comercial", payload);
      toast.success(`OT Comercial ${res.data.folio} creada exitosamente`);
      navigate(`/ots-comercial/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear la OT");
    } finally {
      setSaving(false);
    }
  };

  const isBundleMode = stats?.tipo_ot === "bundle";

  return (
    <div className="space-y-4" data-testid="otac-upload-page">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Cargar OT Comercial desde Excel</h1>
        <p className="text-sm text-muted-foreground">Descarga la plantilla, llénala y cárgala para crear una OT Comercial.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Paso 1: Descargar plantilla */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Download className="h-4 w-4 text-primary" /> 1. Descarga la plantilla
            </CardTitle>
            <CardDescription className="text-xs">Plantilla Excel con hojas para Cambio de Clave y Bundle.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={downloadTemplate} data-testid="otac-template-download" className="w-full">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Descargar plantilla_OT_Comercial.xlsx
            </Button>
            <ul className="text-xs text-muted-foreground mt-3 space-y-1 list-disc list-inside">
              <li>Hoja <strong>"OT_Header"</strong>: datos generales (folio, cliente, tipo...)</li>
              <li>Hoja <strong>"Items_CambioClave"</strong>: partidas de cambio de clave</li>
              <li>Hoja <strong>"Items_Bundle"</strong>: bundles con componentes</li>
              <li>Hoja <strong>"Catalogos"</strong>: valores de referencia</li>
            </ul>
          </CardContent>
        </Card>

        {/* Paso 2: Subir archivo */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <Upload className="h-4 w-4 text-primary" /> 2. Carga el archivo lleno
            </CardTitle>
            <CardDescription className="text-xs">Te mostraremos un preview antes de crear la OT.</CardDescription>
          </CardHeader>
          <CardContent>
            <label
              htmlFor="otac-file-upload"
              className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-md py-8 hover:border-primary/60 transition-colors cursor-pointer bg-muted/20"
              data-testid="otac-file-dropzone"
            >
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-sm">{file ? file.name : "Haz clic o arrastra tu archivo .xlsx"}</span>
              <span className="text-xs text-muted-foreground mt-1">Solo archivos Excel (.xlsx)</span>
              <input
                id="otac-file-upload"
                ref={inputRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={onFileChange}
                className="hidden"
                data-testid="otac-file-input"
              />
            </label>
            {uploading && <p className="text-xs text-muted-foreground mt-2 text-center animate-pulse">Procesando archivo...</p>}
          </CardContent>
        </Card>
      </div>

      {/* Paso 3: Preview */}
      {preview && stats && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-semibold">3. Preview — Confirma antes de crear</CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={reset} data-testid="otac-upload-cancel">
                  <X className="h-4 w-4 mr-1" /> Cancelar
                </Button>
                <Button size="sm" onClick={confirmCreate} disabled={saving} data-testid="otac-upload-confirm">
                  <Check className="h-4 w-4 mr-1" /> {saving ? "Creando..." : "Crear OT Comercial"}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <Alert>
              <AlertDescription className="text-xs">
                {preview.folio
                  ? `Se creará la OT con folio: ${preview.folio}`
                  : "Se creará una OT con folio automático."}
                {" "}Tipo: <strong>{isBundleMode ? "Bundle" : "Cambio de Clave"}</strong>
              </AlertDescription>
            </Alert>

            {/* Datos generales */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Folio" value={preview.folio || "(Auto)"} mono />
              <Stat label="Tipo OT" value={isBundleMode ? "Bundle" : "Cambio de Clave"} />
              <Stat label="Cliente" value={preview.cliente} />
              <Stat label="Solicitante" value={preview.solicitante} />
              <Stat label="Fecha Compromiso" value={preview.fecha_compromiso} mono />
              <Stat label="Forma de Pago" value={preview.forma_pago} />
              <Stat label="Partidas" value={stats.item_count} mono />
              <Stat label="Total QTY" value={stats.total_qty} mono />
              <Stat label="Total Costo" value={fmtMoney(stats.total_costo)} mono />
              <Stat label="SKUs" value={stats.unique_skus} mono />
              {isBundleMode && <Stat label="Componentes" value={stats.total_componentes} mono />}
            </div>

            {/* Tabla de items */}
            <div className="rounded-md border border-border overflow-x-auto">
              {!isBundleMode ? (
                <table className="w-full text-xs">
                  <thead className="bg-muted/40">
                    <tr className="text-left uppercase tracking-wider text-muted-foreground">
                      <th className="py-2 px-2">#</th>
                      <th className="py-2 px-2">SKU</th>
                      <th className="py-2 px-2">VPN</th>
                      <th className="py-2 px-2">Descripción</th>
                      <th className="py-2 px-2">Clave Vieja</th>
                      <th className="py-2 px-2">Clave Nueva</th>
                      <th className="py-2 px-2">UPC</th>
                      <th className="py-2 px-2 text-right">QTY</th>
                      <th className="py-2 px-2 text-right">Costo/Pza</th>
                      <th className="py-2 px-2">Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.items.map((it, i) => (
                      <tr key={i} className="border-t border-border/60" data-testid={`otac-upload-row-${i}`}>
                        <td className="py-1.5 px-2 font-mono text-muted-foreground">{i + 1}</td>
                        <td className="py-1.5 px-2 font-mono">{it.sku || "-"}</td>
                        <td className="py-1.5 px-2">{it.vpn || "-"}</td>
                        <td className="py-1.5 px-2">{it.descripcion || "-"}</td>
                        <td className="py-1.5 px-2 font-mono">{it.clave_vieja || "-"}</td>
                        <td className="py-1.5 px-2 font-mono">{it.clave_nueva || "-"}</td>
                        <td className="py-1.5 px-2 font-mono">{it.upc || "-"}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{it.qty || 0}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{fmtMoney(it.costo_pieza)}</td>
                        <td className="py-1.5 px-2 text-muted-foreground">{it.observaciones || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="divide-y divide-border">
                  {preview.items.map((it, i) => (
                    <div key={i} className="p-2 space-y-1" data-testid={`otac-upload-bundle-${i}`}>
                      <div className="flex items-center gap-2 text-xs">
                        <Badge variant="secondary" className="text-[10px]">
                          <Package className="h-3 w-3 mr-1" /> Bundle {i + 1}
                        </Badge>
                        <span className="font-mono font-medium">{it.sku}</span>
                        <span className="text-muted-foreground">{it.vpn}</span>
                        <span className="text-muted-foreground">{it.descripcion}</span>
                        <span className="ml-auto font-mono">QTY: {it.qty}</span>
                        <span className="font-mono text-primary font-semibold">{fmtMoney(it.qty * it.costo_pieza)}</span>
                      </div>
                      {(it.componentes || []).length > 0 && (
                        <div className="ml-6 text-[11px]">
                          <span className="text-muted-foreground text-[10px] uppercase">Componentes:</span>
                          <table className="w-full mt-0.5">
                            <tbody>
                              {it.componentes.map((c, ci) => (
                                <tr key={ci} className="text-muted-foreground">
                                  <td className="py-0.5 pr-2 font-mono">{c.sku}</td>
                                  <td className="py-0.5 pr-2">{c.vpn}</td>
                                  <td className="py-0.5 pr-2 font-mono">{c.upc}</td>
                                  <td className="py-0.5 text-right font-mono">{c.qty} pzs</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
